import { HttpAgent } from '@icp-sdk/core/agent'
import OdinClient from '../clients/OdinClient'
import { CKBTC_LEDGER, ODIN_BACKEND_CANISTER } from '../constants/canisterIds'
import { AMOUNT_TOO_LOW } from '../constants/swapErrors'
import { CKBTC_ICP_ID, ODINBTC_ID } from '../constants/tokenIds'
import { approveToken } from '../handlers/walletActions/wallet'
import { PlugToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
} from '../interfaces/swapInterface'
import { Wallet } from '../providers/WalletProvider'

const ODIN_FEE = 0.000001 // written 0.0000201 BTC on website, but in reality 100 sats
const MIN_WITHDRAW_AMOUNT = 5000000n
const MIN_DEPOSIT_AMOUNT = 3000n

class OdinBridgeClient {
  agent: HttpAgent

  static get supportedTokens() {
    return [[CKBTC_ICP_ID, ODINBTC_ID]]
  }

  static isSupportedPair(fromToken: PlugToken, toToken: PlugToken) {
    return this.supportedTokens.some(
      (tokenIds) => tokenIds.includes(fromToken.id) && tokenIds.includes(toToken.id)
    )
  }

  constructor(agent: HttpAgent) {
    this.agent = agent
  }

  getQuote(amount: bigint, fromToken: PlugToken): SwapProviderQuote | SwapProviderQuoteFetchError {
    const minAmount = fromToken.id === CKBTC_ICP_ID ? MIN_DEPOSIT_AMOUNT : MIN_WITHDRAW_AMOUNT

    if (amount < minAmount) {
      return { error: AMOUNT_TOO_LOW, provider: SwapProvider.ODIN }
    } else {
      const price = amount - BigInt(ODIN_FEE * 10 ** fromToken.decimals)

      return {
        provider: SwapProvider.ODIN,
        price: Number(price),
        priceHuman: Number(price) * 10 ** -fromToken.decimals,
      }
    }
  }

  async swap(fromToken: PlugToken, amount: bigint, wallet: Wallet) {
    const isDeposit = fromToken.id === CKBTC_ICP_ID
    const odinClient = new OdinClient(this.agent)

    if (isDeposit) {
      await approveToken({
        spender: { address: ODIN_BACKEND_CANISTER },
        tokenCanisterId: CKBTC_LEDGER,
        standard: fromToken.standard,
        amount: amount.toString(),
      })

      const depositResult = await odinClient.depositCkBTC(amount)

      if ('ok' in depositResult) {
        return Number(depositResult.ok)
      } else {
        throw new Error(depositResult.err)
      }
    } else {
      const withdrawResult = await odinClient.withdrawToken(
        'btc',
        wallet.principalId,
        amount.toString()
      )

      if ('ok' in withdrawResult) {
        return Number(amount)
      } else {
        throw new Error(withdrawResult.err)
      }
    }
  }
}

export default OdinBridgeClient
